import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import { BRAND_IMAGES, getBrandImageUrl } from '@/app/common/contentHubAssets.ts';
import type { Localization } from '../../common/Localization';
import { isValidDate } from '../../Utilities';
import getSearchParams from '../../common/getSearchParams';
import type { HandleFiltersChanged } from '../SearchPageContainer';
import { removeSearchParams } from '../Filters/utils';

const isDateInThePast = (date: string, today: string) => date < today;

// The instant is shared so the server and the client resolve the same day (MPH-1120).
export const isSearchInThePast = (searchParams: SearchParams, instant?: string) => {
  const today = (instant || new Date().toISOString()).slice(0, 10);
  const dateFrom = searchParams[URLParameters.DATE_FROM];
  const dateTo = searchParams[URLParameters.DATE_TO];
  if (dateFrom?.match(/^\d{4}-\d{2}-\d{2}$/) !== null && isValidDate(new Date(dateFrom))) {
    return isDateInThePast(dateFrom, today) || isDateInThePast(dateTo, today);
  }
  if (dateFrom === 'A') {
    return false;
  }
  if (dateFrom?.indexOf('M,') === 0) {
    const currentYear = parseInt(today.slice(0, 4), 10);
    const currentMonth = parseInt(today.slice(5, 7), 10);
    const values = dateFrom.split('M,')[1].split(',');
    const validValues = values.filter((x) => {
      const isYYYYMM = x.match(/^\d{4}-\d{2}$/);
      if (isYYYYMM) {
        const year = parseInt(x.split('-')[0], 10);
        const month = parseInt(x.split('-')[1], 10);
        if ((year === currentYear && month > 0 && month <= 12 && month >= currentMonth) || year === currentYear + 1) {
          return true;
        }
      }
      return false;
    });
    if (validValues.length === values.length) {
      return false;
    }
  }
  return true;
};

const getNoResultsIcon = ({
  hasFilters,
  searchIsInThePast,
  shouldResetMap,
}: {
  hasFilters: boolean;
  searchIsInThePast: boolean;
  shouldResetMap: boolean;
}): BRAND_IMAGES => {
  if (shouldResetMap) return BRAND_IMAGES.NO_RESULTS;
  if (searchIsInThePast) return BRAND_IMAGES.TIME_TRAVEL;
  if (hasFilters) return BRAND_IMAGES.OVER_FILTERED;
  return BRAND_IMAGES.NO_RESULTS;
};

export const getNoResultsImage = ({
  localization,
  hasFilters,
  searchIsInThePast,
  shouldResetMap,
}: {
  localization: Localization;
  hasFilters: boolean;
  searchIsInThePast: boolean;
  shouldResetMap: boolean;
}) => {
  const image = getNoResultsIcon({ hasFilters, searchIsInThePast, shouldResetMap });
  return getBrandImageUrl(image, localization);
};

export const getSubTitle = ({
  isApp,
  hasFilters,
  searchIsInThePast,
  shouldResetMap,
  t,
}: {
  isApp: boolean;
  hasFilters: boolean;
  searchIsInThePast: boolean;
  shouldResetMap: boolean;
  t: TranslationFunction;
}) => {
  let subTitleText = t('vacation.oss.noresults.subtitle');
  if (isApp) {
    subTitleText = t('vacation.oss.noresults.subtitle.app');
  }
  if (hasFilters) {
    subTitleText = t('vacation.oss.noresults.subtitle_overfiltered');
  }
  if (searchIsInThePast) {
    subTitleText = t('vacation.oss.noresults.subtitle_expired');
  }
  let titleText = searchIsInThePast ? t('vacation.oss.noresults.title_expired') : t('vacation.oss.noresults.title');
  if (shouldResetMap) {
    titleText = t('vacation.oss.noresults.title_toofar');
    subTitleText = t('vacation.oss.noresults.subtitle_toofar');
  }

  return { subTitleText, titleText };
};

export const handleDeleteBadge = ({
  name,
  handleFiltersChanged,
  value,
}: {
  name: URLParameters;
  handleFiltersChanged: (params: HandleFiltersChanged) => void;
  value?: string;
}) => {
  const revisedQueryParams = getSearchParams();
  if (
    name === URLParameters.ACCOMODATION_THEME ||
    name === URLParameters.ACCOMODATION_CHAIN ||
    name === URLParameters.ACCOMODATION_STATION ||
    name === URLParameters.ACCOMODATION_DISTRICT ||
    name === URLParameters.ACCOMODATION_SKILIFT ||
    name === URLParameters.ACCOMODATION_BEACH ||
    name === URLParameters.ACCOMODATION_FACILITIES ||
    name === URLParameters.GENERAL_ACCOMODATION_FACILITIES ||
    name === URLParameters.FAMILY_ACCOMODATION_FACILITIES ||
    name === URLParameters.ACTIVITY_ACCOMODATION_FACILITIES ||
    name === URLParameters.WELLNESS_ACCOMODATION_FACILITIES ||
    name === URLParameters.FOOD_ACCOMODATION_FACILITIES ||
    name === URLParameters.ACCOMODATION_TYPE ||
    name === URLParameters.STARS ||
    name === URLParameters.MEAL ||
    name === URLParameters.RATING ||
    name === URLParameters.FAUNA ||
    name === URLParameters.BEACH_TYPE ||
    name === URLParameters.WATERSPORT ||
    name === URLParameters.ENTERTAINMENTS ||
    name === URLParameters.WATER_WAVES
  ) {
    const valueArray = revisedQueryParams[name].split(',');
    if (valueArray.length > 1) {
      const index = valueArray.findIndex((item) => item === value);
      valueArray.splice(index, 1);
      revisedQueryParams[name] = valueArray.join();
    } else {
      delete revisedQueryParams[name];
    }
  } else {
    delete revisedQueryParams[name];
  }

  const appliedFilters = removeSearchParams(revisedQueryParams);

  handleFiltersChanged({ searchParams: revisedQueryParams, appliedFilters });
};
